import { useEffect, useState } from 'react';
import { Entity } from '@backstage/catalog-model';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { CHOREO_ANNOTATIONS } from '@openchoreo/backstage-plugin-common';
import { openChoreoClientApiRef } from '../../../api/OpenChoreoClientApi';

/** Per-environment deployment status, shared by Components and Resources. */
export interface EnvironmentDeploymentStatus {
  isDeployed: boolean;
  /** Status from the (Resource)ReleaseBinding: Ready, NotReady, Failed, etc. */
  status?: string;
  statusReason?: string;
  statusMessage?: string;
}

/** Deployment status keyed by lowercased environment name. */
export type DeploymentStatusByEnv = Record<string, EnvironmentDeploymentStatus>;

/** Entity kinds surfaced in the project's "Project Contents" table. */
export type ProjectContentKind = 'component' | 'resource';

/** A single row in the unified Project Contents table. */
export interface ProjectContentItem {
  entity: Entity;
  kind: ProjectContentKind;
  name: string;
  displayName: string;
  type: string;
  description: string;
  deploymentStatus: DeploymentStatusByEnv;
  /**
   * False until this row's release bindings have been fetched. Rows render from
   * catalog data first; the Deployment column shows a skeleton until this flips.
   */
  deploymentLoaded: boolean;
  createdAt?: string;
}

/** The catalog field a column maps to for server-side ordering. */
export type ProjectContentsOrderBy = 'name' | 'createdAt';

export interface ProjectContentsPageParams {
  systemEntity: Entity;
  /** Free-text search term (already debounced by the caller). */
  search: string;
  /** Selected kinds (lowercased, e.g. "component"); `null` means "all kinds". */
  kinds: Set<string> | null;
  /** Selected `spec.type` values; `null` means "all types". */
  types: Set<string> | null;
  orderBy: ProjectContentsOrderBy;
  orderDir: 'asc' | 'desc';
  /** Cursor for the page to fetch; omit/undefined for the first page. */
  cursor?: string;
  limit: number;
}

export interface ProjectContentsPageResult {
  items: ProjectContentItem[];
  totalItems: number;
  prevCursor?: string;
  nextCursor?: string;
  loading: boolean;
  error: Error | null;
}

type BindingLike = {
  environment?: string;
  status?: string;
  statusReason?: string;
  statusMessage?: string;
};

function toDeploymentStatus(bindings?: BindingLike[]): DeploymentStatusByEnv {
  const status: DeploymentStatusByEnv = {};
  for (const binding of bindings ?? []) {
    const env = binding.environment?.toLowerCase();
    if (env) {
      status[env] = {
        isDeployed: true,
        status: binding.status,
        statusReason: binding.statusReason,
        statusMessage: binding.statusMessage,
      };
    }
  }
  return status;
}

const ALL_KINDS = ['Component', 'Resource'];

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function buildFilter(
  project: string,
  namespace: string,
  kinds: Set<string> | null,
  types: Set<string> | null,
) {
  const filter: Record<string, string | string[]> = {
    kind: kinds && kinds.size > 0 ? [...kinds].map(capitalize) : ALL_KINDS,
    'spec.system': project,
    'metadata.namespace': namespace,
  };
  if (types && types.size > 0) {
    filter['spec.type'] = [...types];
  }
  return filter;
}

function buildOrderFields(
  orderBy: ProjectContentsOrderBy,
  orderDir: 'asc' | 'desc',
) {
  // IMPORTANT: keep this to a SINGLE order field. The catalog only takes its
  // cursor-safe "fast path" when `order.length <= 1`; adding a tiebreak field
  // forces a multi-join path that paginates incorrectly with cursors. The
  // backend already appends a stable entity-id tiebreak on the fast path.
  //
  // Name sorts by `metadata.title` (not `metadata.name`) because that is the
  // value rendered in the column (the catalog provider always sets
  // title = displayName || name), so the visible order stays alphabetical.
  // created-at is ISO-8601, so the index's lexical order is chronological.
  const field =
    orderBy === 'createdAt'
      ? `metadata.annotations.${CHOREO_ANNOTATIONS.CREATED_AT}`
      : 'metadata.title';
  return [{ field, order: orderDir }];
}

/**
 * Fetches a single, server-paginated page of the project's Components and
 * Resources from the catalog (filtering, full-text search and ordering all run
 * server-side), then enriches only that page's rows with deployment status.
 *
 * Changing any filter/search/sort should reset the caller's `cursor` to
 * `undefined` so the next fetch starts from the first page.
 */
export function useProjectContentsPage(
  params: ProjectContentsPageParams,
): ProjectContentsPageResult {
  const catalogApi = useApi(catalogApiRef);
  const client = useApi(openChoreoClientApiRef);

  const {
    systemEntity,
    search,
    kinds,
    types,
    orderBy,
    orderDir,
    cursor,
    limit,
  } = params;
  const project = systemEntity.metadata.name;
  const namespace =
    systemEntity.metadata.annotations?.[CHOREO_ANNOTATIONS.NAMESPACE];
  // Stable primitive dependencies for the Set inputs.
  const kindsKey = kinds ? [...kinds].sort().join(' ') : '*';
  const typesKey = types ? [...types].sort().join(' ') : '*';

  const [result, setResult] = useState<ProjectContentsPageResult>({
    items: [],
    totalItems: 0,
    loading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    const empty = {
      items: [],
      totalItems: 0,
      prevCursor: undefined,
      nextCursor: undefined,
    };

    // No project context, or the user has explicitly cleared every kind/type.
    if (
      !project ||
      !namespace ||
      (kinds !== null && kinds.size === 0) ||
      (types !== null && types.size === 0)
    ) {
      setResult({ ...empty, loading: false, error: null });
      return undefined;
    }

    setResult(prev => ({ ...prev, loading: true, error: null }));

    const toBaseItem = (entity: Entity): ProjectContentItem => {
      const kindValue: ProjectContentKind =
        entity.kind.toLowerCase() === 'resource' ? 'resource' : 'component';
      return {
        entity,
        kind: kindValue,
        name: entity.metadata.name,
        displayName: entity.metadata.title || entity.metadata.name,
        type: String((entity.spec as { type?: unknown })?.type ?? ''),
        description: entity.metadata.description ?? '',
        deploymentStatus: {},
        deploymentLoaded: false,
        createdAt:
          entity.metadata.annotations?.[CHOREO_ANNOTATIONS.CREATED_AT] ||
          undefined,
      };
    };

    const fetchDeploymentStatus = async (
      item: ProjectContentItem,
    ): Promise<DeploymentStatusByEnv> => {
      try {
        const response =
          item.kind === 'component'
            ? await client.fetchReleaseBindings(item.entity)
            : await client.fetchResourceReleaseBindings(item.entity);
        return toDeploymentStatus(response?.data?.items);
      } catch {
        // Leave deployment status empty if bindings can't be fetched.
        return {};
      }
    };

    (async () => {
      try {
        // A cursor request carries the original filter/order in the cursor, so
        // we must not re-send them; a first-page request builds them fresh.
        const response = cursor
          ? await catalogApi.queryEntities({ cursor, limit })
          : await catalogApi.queryEntities({
              limit,
              filter: buildFilter(project, namespace, kinds, types),
              orderFields: buildOrderFields(orderBy, orderDir),
              fullTextFilter: search
                ? {
                    // Search by name only (displayed title + entity name).
                    term: search,
                    fields: ['metadata.title', 'metadata.name'],
                  }
                : undefined,
            });
        if (cancelled) return;

        // Phase 1: render rows immediately from the catalog payload. The
        // Deployment column shows a skeleton until bindings resolve.
        const baseItems = response.items.map(toBaseItem);
        setResult({
          items: baseItems,
          totalItems: response.totalItems,
          prevCursor: response.pageInfo.prevCursor,
          nextCursor: response.pageInfo.nextCursor,
          loading: false,
          error: null,
        });

        // Phase 2: enrich only the visible page's rows with deployment status.
        const statuses = await Promise.all(
          baseItems.map(fetchDeploymentStatus),
        );
        if (cancelled) return;
        setResult(prev => ({
          ...prev,
          items: prev.items.map((item, index) => ({
            ...item,
            deploymentStatus: statuses[index] ?? {},
            deploymentLoaded: true,
          })),
        }));
      } catch (err) {
        if (!cancelled) {
          setResult({ ...empty, loading: false, error: err as Error });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    project,
    namespace,
    search,
    kindsKey,
    typesKey,
    orderBy,
    orderDir,
    cursor,
    limit,
    catalogApi,
    client,
  ]);

  return result;
}
